import { useCallback, useEffect, useRef, useState } from "react";

export type ToastKind = "ok" | "warn" | "error";

export interface Toast {
  id: number;
  kind: ToastKind;
  title: string;
  body?: string;
}

const MAX_TOASTS = 3;

/** Errors linger a little longer than confirmations. */
export function toastTtlMs(kind: ToastKind): number {
  return kind === "error" ? 6500 : 4500;
}

/** Append a toast, keeping only the newest MAX_TOASTS. */
export function capToasts(prev: Toast[], next: Toast): Toast[] {
  return [...prev.slice(1 - MAX_TOASTS), next];
}

export function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const timers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    clearTimeout(timers.current[id]);
    delete timers.current[id];
  }, []);

  const push = useCallback(
    (toast: Omit<Toast, "id">) => {
      const id = nextId.current++;
      setToasts((prev) => capToasts(prev, { ...toast, id }));
      timers.current[id] = setTimeout(() => dismiss(id), toastTtlMs(toast.kind));
    },
    [dismiss],
  );

  // clear pending timers on unmount
  useEffect(
    () => () => {
      for (const t of Object.values(timers.current)) clearTimeout(t);
    },
    [],
  );

  return { toasts, push, dismiss };
}

interface ToastsProps {
  toasts: Toast[];
  onDismiss: (id: number) => void;
}

export function Toasts({ toasts, onDismiss }: ToastsProps) {
  return (
    <div className="kl-toasts" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={`kl-toast ${t.kind}`} role="status">
          <span className="kl-toast-ic">{t.kind === "ok" ? "✓" : "!"}</span>
          <div className="kl-toast-body">
            <div className="kl-toast-title">{t.title}</div>
            {t.body ? <div className="kl-toast-sub">{t.body}</div> : null}
          </div>
          <button
            type="button"
            className="kl-toast-x"
            onClick={() => onDismiss(t.id)}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}
